import { useState, useEffect } from 'react';
import { api, User, Organization } from '../api';
import { validatePassword, PASSWORD_REQUIREMENTS, PasswordToggleButton, getErrorMessage } from '../../../shared/src';

interface ProfilePageProps {
  user: User | null;
  organization: Organization | null;
  onUpdate: () => void;
}

export function ProfilePage({ user, organization, onUpdate }: ProfilePageProps) {
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await api.updateMe({ firstName, lastName });
      setMessage('Profile updated successfully');
      onUpdate();
      setIsEditing(false);
    } catch (err: unknown) {
      setMessage(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
    setMessage('');
    setIsEditing(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setPasswordMessage(passwordValidation.errors.join('\n'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage('New passwords do not match');
      return;
    }

    setPasswordSaving(true);

    try {
      await api.changePassword(currentPassword, newPassword);
      setPasswordMessage('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    } catch (err: unknown) {
      setPasswordMessage(getErrorMessage(err));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handlePasswordCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordMessage('');
    setIsChangingPassword(false);
  };

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  const getFullName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user?.email || 'User';
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeClass = (role: string) => {
    return role === 'org_admin' ? 'role-badge admin' : 'role-badge user';
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'org_admin':
        return 'Admin';
      case 'user':
        return 'Member';
      default:
        return role;
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Profile</h1>
        <p className="subtitle">Manage your personal information</p>
      </div>

      {/* Profile Header Card */}
      <div className="profile-header-card">
        <div className="profile-avatar-large">
          {getInitials()}
        </div>
        <div className="profile-header-info">
          <div className="profile-name-row">
            <h2 className="profile-name">{getFullName()}</h2>
            <button
              className="edit-icon-btn"
              onClick={() => setIsEditing(!isEditing)}
              title="Edit profile"
              aria-label="Edit profile"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          </div>
          <p className="profile-email">{user?.email}</p>
          <div className="profile-badges">
            <span className={getRoleBadgeClass(user?.role || 'user')}>
              {getRoleDisplay(user?.role || 'user')}
            </span>
          </div>
        </div>
      </div>

      {/* Edit Profile (collapsible) */}
      {isEditing && (
        <div className="profile-section">
          <h3 className="section-title">Edit Profile</h3>
          <div className="profile-edit-card">
            {message && (
              <div className={message.includes('success') ? 'success-message' : 'error-message'}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={user?.email || ''} disabled />
                <small>
                  {organization?.auth_method === 'sso'
                    ? 'Email is managed by your identity provider'
                    : user?.role === 'org_admin'
                      ? 'To change email, create a new user in Admin > Users and remove this one'
                      : 'Contact an administrator to change your email'}
                </small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Details */}
      <div className="profile-section">
        <h3 className="section-title">Account Details</h3>
        <div className="profile-details-grid">
          <div className="profile-detail-item">
            <span className="detail-label">Organization</span>
            <span className="detail-value">{organization?.name || 'Unknown'}</span>
          </div>
          <div className="profile-detail-item">
            <span className="detail-label">Member Since</span>
            <span className="detail-value">{formatDate(user?.created_at)}</span>
          </div>
          <div className="profile-detail-item">
            <span className="detail-label">Last Login</span>
            <span className="detail-value">{formatDateTime(user?.last_login_at)}</span>
          </div>
          <div className="profile-detail-item">
            <span className="detail-label">Authentication</span>
            <span className="detail-value">
              {organization?.auth_method === 'sso' ? 'Single Sign-On (SSO)' : 'Email & Password'}
            </span>
          </div>
        </div>
      </div>

      {/* Security Section - Only for password auth */}
      {organization?.auth_method === 'password' && (
        <div className="profile-section">
          <h3 className="section-title">Security</h3>

          {passwordMessage && (
            <div className={passwordMessage.includes('success') ? 'success-message' : 'error-message'}>
              {passwordMessage.includes('\n') ? (
                passwordMessage.split('\n').map((msg, i) => <div key={i}>{msg}</div>)
              ) : (
                passwordMessage
              )}
            </div>
          )}

          {!isChangingPassword && (
            <div className="security-action-card">
              <div className="security-action-info">
                <span className="security-action-label">Password</span>
                <span className="security-action-description">Update your password to keep your account secure</span>
              </div>
              <button
                className="btn-secondary"
                onClick={() => setIsChangingPassword(true)}
              >
                Change Password
              </button>
            </div>
          )}

          {isChangingPassword && (
            <div className="profile-edit-card">
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                    />
                    <PasswordToggleButton visible={showCurrentPassword} onToggle={() => setShowCurrentPassword(!showCurrentPassword)} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        required
                      />
                      <PasswordToggleButton visible={showNewPassword} onToggle={() => setShowNewPassword(!showNewPassword)} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                      />
                      <PasswordToggleButton visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
                    </div>
                  </div>
                </div>

                <small className="password-requirements-hint">
                  {PASSWORD_REQUIREMENTS.join(' • ')}
                </small>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={handlePasswordCancel}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={passwordSaving}>
                    {passwordSaving ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
